import { Package, StickyNote } from "lucide-react";

/**
 * Renders structured items + notes lists on order cards.
 * Falls back to legacy `description` (rendered under "Açıklamalar") for old orders.
 *
 * Props:
 *  - order: { items?: string[], notes?: string[], description?: string }
 *  - testIdPrefix: string used to build deterministic data-testid values
 *  - dense: boolean — slightly smaller spacing
 */
export function OrderLines({ order, testIdPrefix, dense = false }) {
  const items = Array.isArray(order?.items) ? order.items.filter(Boolean) : [];
  let notes = Array.isArray(order?.notes) ? order.notes.filter(Boolean) : [];

  // Legacy fallback — old orders only had `description`
  if (notes.length === 0 && order?.description) {
    const split = String(order.description)
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (split.length) notes = split;
  }

  if (items.length === 0 && notes.length === 0) return null;

  const gap = dense ? "mt-2 space-y-2" : "mt-3 space-y-3";

  return (
    <div className={gap} data-testid={`${testIdPrefix}-lines`}>
      {items.length > 0 && (
        <LineSection
          icon={<Package size={11} strokeWidth={2.5} />}
          label="Ürünler"
          lines={items}
          testId={`${testIdPrefix}-items`}
          tone="zinc"
        />
      )}
      {notes.length > 0 && (
        <LineSection
          icon={<StickyNote size={11} strokeWidth={2.5} />}
          label="Açıklamalar"
          lines={notes}
          testId={`${testIdPrefix}-notes`}
          tone="amber"
        />
      )}
    </div>
  );
}

function LineSection({ icon, label, lines, testId, tone }) {
  const palette = tone === "amber"
    ? { bg: "bg-amber-50", border: "border-amber-300", chipBg: "bg-amber-200", chipText: "text-amber-900", title: "text-amber-900" }
    : { bg: "bg-zinc-50", border: "border-zinc-300", chipBg: "bg-zinc-900",  chipText: "text-white",      title: "text-zinc-900" };

  return (
    <div
      data-testid={testId}
      className={`border-2 ${palette.border} ${palette.bg} px-3 py-2`}
    >
      <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${palette.title} mb-1.5`}>
        {icon}
        {label}
        <span className={`ml-auto inline-flex items-center justify-center min-w-[18px] h-4 px-1 ${palette.chipBg} ${palette.chipText} text-[10px] font-bold`}>
          {lines.length}
        </span>
      </div>
      <ul className="space-y-1">
        {lines.map((line, i) => (
          <li
            key={i}
            data-testid={`${testId}-row-${i}`}
            className="flex gap-2 text-[14px] leading-snug text-zinc-900"
          >
            <span className="font-mono text-zinc-400 shrink-0 select-none">•</span>
            <span className="break-words">{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
